<template>
  <div v-if="currentUser" class="usefultips-container">
    <div class="page-header">
      <div>
        <h1>Porady</h1>
        <p>Zbiór wszystkich podpowiedzi dostępnych w serwisie.</p>
      </div>

      <button type="button" class="btn-outline" @click="getHints">
        Odśwież
      </button>
    </div>

    <div class="section-card">
      <div class="section-card-header">
        <h2>Najczęściej przydatne wskazówki</h2>
        <p class="section-subtitle">
          Rozwiń wybraną pozycję, aby zobaczyć pełną instrukcję krok po kroku.
        </p>
      </div>

      <div v-if="isLoading" class="empty-state">
        <p>Ładowanie porad...</p>
      </div>

      <div v-else-if="activeHints.length > 0" class="tips-accordion">
        <div
            v-for="hint in activeHints"
            :key="hint.id"
            class="tip-item"
            :class="{ 'tip-item--open': openedHintId === hint.id }"
        >
          <button
              type="button"
              class="tip-header"
              @click="toggleHint(hint.id)"
              :aria-expanded="openedHintId === hint.id"
          >
            <span class="tip-header__content">
              <span class="tip-title">{{ getHintTitle(hint) }}</span>
              <span class="tip-meta">
                {{ hint.page_name || hint.page_key }}
                <template v-if="hint.steps && hint.steps.length">
                  · {{ hint.steps.length }} {{ getStepsLabel(hint.steps.length) }}
                </template>
              </span>
            </span>

            <span class="tip-arrow" aria-hidden="true">
              {{ openedHintId === hint.id ? '−' : '+' }}
            </span>
          </button>

          <div v-if="openedHintId === hint.id" class="tip-body">
            <div v-if="hint.steps && hint.steps.length > 0" class="steps-list">
              <div
                  v-for="(step, index) in sortedSteps(hint.steps)"
                  :key="step.id"
                  class="step-card"
              >
                <div class="step-number">
                  {{ index + 1 }}
                </div>

                <div class="step-content">
                  <h3>{{ step.title }}</h3>

                  <div v-if="step.image_path" class="step-image-wrapper">
                    <img
                        :src="getImageUrl(step.image_path)"
                        :alt="step.title"
                        class="step-image"
                    >
                  </div>

                  <div class="step-text" v-html="step.content"></div>
                </div>
              </div>
            </div>

            <div v-else class="hint-empty">
              Brak kroków dla tej podpowiedzi.
            </div>
          </div>
        </div>
      </div>

      <div v-else class="empty-state">
        <p>Brak porad do wyświetlenia.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { useMeta } from 'vue-meta';
import { useAuthStore } from '@/stores/auth';
import { useAppStore } from '@/stores/app';
import hintService from '@/services/hint.service';

export default {
  name: 'UsefulTips',
  setup() {
    useMeta({ title: 'Porady' });

    return {
      authStore: useAuthStore(),
      appStore: useAppStore(),
    };
  },
  data() {
    return {
      hints: [],
      openedHintId: null,
      isLoading: false,
    };
  },
  computed: {
    currentUser() {
      return this.authStore.user;
    },
    activeHints() {
      return this.hints.filter(hint => hint.is_active);
    },
  },
  mounted() {
    if (!this.currentUser) {
      this.$router.push('/login');
      return;
    }

    if (this.currentUser.role_id !== 2) {
      this.$router.push('/login');
      return;
    }

    if (!this.appStore.isSubscribed) {
      this.$router.push('/dla-biznesu/profile/user-data');
      return;
    }

    this.getHints();
  },
  methods: {
    getHints() {
      this.isLoading = true;

      hintService.getAll()
          .then(res => {
            const hints = Array.isArray(res.data) ? res.data : res.data.items || [];
            this.hints = hints;
            this.openedHintId = this.activeHints[0]?.id || null;
          })
          .catch(() => {
            this.hints = [];
            this.appStore.setIsAlert('Nie udało się pobrać porad.');
          })
          .finally(() => {
            this.isLoading = false;
          });
    },
    toggleHint(hintId) {
      this.openedHintId = this.openedHintId === hintId ? null : hintId;
    },
    sortedSteps(steps) {
      return [...steps].sort((a, b) => Number(a.order) - Number(b.order));
    },
    getHintTitle(hint) {
      if (hint.page_name) {
        return hint.page_name;
      }

      if (hint.steps && hint.steps.length > 0) {
        return this.sortedSteps(hint.steps)[0].title;
      }

      return hint.page_key;
    },
    getStepsLabel(count) {
      if (count === 1) return 'krok';
      if (count > 1 && count < 5) return 'kroki';
      return 'kroków';
    },
    getImageUrl(path) {
      if (!path) return '';
      if (path.startsWith('http')) return path;
      return `${process.env.VUE_APP_STORAGE_URL || ''}${path}`;
    },
  },
};
</script>

<style scoped lang="scss">
.usefultips-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 16px;
  font-family: sans-serif;
  box-sizing: border-box;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e5e7eb;
}

.page-header h1 {
  font-size: 2.8rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 6px;
}

.page-header p {
  margin: 0;
  color: #6b7280;
  font-size: 1.4rem;
}

.section-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.08);
  padding: 24px;
  box-sizing: border-box;
  width: 100%;
}

.section-card-header {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 18px;
}

.section-card h2 {
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.section-subtitle {
  margin: 0;
  color: #6b7280;
  font-size: 1.4rem;
}

.tips-accordion {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.tip-item {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #ffffff;
  overflow: hidden;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.tip-item--open {
  border-color: #10b981;
  box-shadow: 0 8px 20px rgba(16, 185, 129, 0.1);
}

.tip-header {
  width: 100%;
  border: none;
  background: #ffffff;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  cursor: pointer;
  text-align: left;
}

.tip-header:hover {
  background: #f9fafb;
}

.tip-header__content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.tip-title {
  font-size: 1.6rem;
  font-weight: 700;
  color: #1f2937;
}

.tip-meta {
  font-size: 1.25rem;
  color: #6b7280;
}

.tip-arrow {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  background: #ecfdf5;
  color: #10b981;
  font-size: 2.2rem;
  line-height: 1;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.tip-body {
  border-top: 1px solid #e5e7eb;
  padding: 18px 16px;
  background: #f9fafb;
}

.steps-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.step-card {
  display: flex;
  gap: 14px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 16px;
}

.step-number {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  background: #f97316;
  color: #ffffff;
  font-size: 1.4rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.step-content {
  min-width: 0;
  flex: 1;
}

.step-content h3 {
  margin: 0 0 10px;
  color: #1f2937;
  font-size: 1.55rem;
  font-weight: 700;
}

.step-image-wrapper {
  margin-bottom: 12px;
}

.step-image {
  display: block;
  max-width: 100%;
  max-height: 340px;
  object-fit: contain;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
}

.step-text {
  color: #374151;
  font-size: 1.35rem;
  line-height: 1.6;
}

.step-text :deep(p) {
  margin: 0 0 8px;
}

.step-text :deep(p:last-child) {
  margin-bottom: 0;
}

.step-text :deep(ul),
.step-text :deep(ol) {
  margin: 8px 0;
  padding-left: 20px;
}

.step-text :deep(a) {
  color: #2563eb;
  text-decoration: none;
}

.step-text :deep(a:hover) {
  text-decoration: underline;
}

.empty-state,
.hint-empty {
  text-align: center;
  padding: 28px;
  color: #9ca3af;
  font-size: 1.5rem;
  border: 1px dashed #d1d5db;
  border-radius: 10px;
  background: #ffffff;
}

.btn-outline {
  background-color: transparent;
  color: #1f2937;
  padding: 10px 16px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  font-size: 1.4rem;
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s;
}

.btn-outline:hover {
  border-color: #10b981;
  color: #10b981;
}

@media (max-width: 640px) {
  .usefultips-container {
    padding: 16px 12px;
  }

  .page-header h1 {
    font-size: 2.2rem;
  }

  .section-card {
    padding: 16px 12px;
    border-radius: 8px;
  }

  .section-card h2 {
    font-size: 1.8rem;
  }

  .tip-header {
    padding: 14px 12px;
  }

  .step-card {
    flex-direction: column;
    padding: 14px;
  }

  .btn-outline {
    width: 100%;
    text-align: center;
  }
}
</style>